import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import parse from 'html-react-parser';
import { useQueryClient } from 'react-query';

import { translate as t } from 'hooks/useTranslations';
import getIssuedManual from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManual';
import useDraftManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';
import useDistributableChanges, { DistributableChange } from './_data_sources/useDistributableChanges';
import useManageDistributableChanges, { DistributableTarget } from './_data_sources/useManageDistributableChanges';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import { Badge, Box, Button, Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { ArrowBackOutlined, ExpandLess, ExpandMore, Close as RemoveIcon } from '@mui/icons-material';
import { QuickSearch } from 'components/_atoms';
import manualHelper from '../../DraftManuals/helpers/draftManualHelper';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import datetimeHelper from 'helpers/datetimeHelper';
import _ from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { neutral } from '_lex/_foundations/phxColors';
import { DraftManual } from 'views/App/Portal/Policy/_types/manuals/draft';
import { restructureDistributableStructure, DistributableParentRow } from './parentChildModel';

const publishSteps = ['select_changes', 'select_targets'];

type ExpandedDistributableChanges = DistributableParentRow & {
  isMainRow?: boolean;
  isExpanded?: boolean;
  parentId?: string;
  currentDistributableChange?: any;
  distributableChangeIndex?: number;
  searchableTitle?: string;
};

type ExtendedDistributableTarget = DistributableTarget & {
  searchableName?: string;
};

const DistributeChanges: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const [draftManual, setDraftManual] = useState<DraftManual | null>(null);
  const draftManualQuery = useDraftManual(id);
  const manualQuery = getIssuedManual(id, { enabled: !!draftManual });
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState<string>(publishSteps[0]);
  const [distributableChanges, setDistributableChanges] = useState<ExpandedDistributableChanges[]>([]);
  const [selectedChanges, setSelectedChanges] = useState<DistributableParentRow[]>([]);
  const [changesSearch, setChangesSearch] = useState<string>('');
  const [showSelectedChanges, setShowSelectedChanges] = useState(false);
  const changesQuery = useDistributableChanges(id || '0', { enabled: !!id });
  const [distributableTargets, setDistributableTargets] = useState<ExtendedDistributableTarget[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<ExtendedDistributableTarget[]>([]);
  const [showSelectedTargets, setShowSelectedTargets] = useState(false);
  const [targetSearch, setTargetSearch] = useState<string>('');
  const [isLoadingTargets, setIsLoadingTargets] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { getDistributeTargets, distributeChanges } = useManageDistributableChanges();

  const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [targetRowSelectionModel, setTargetRowSelectionModel] = React.useState<GridRowSelectionModel>([]);

  useEffect(() => {
    if (changesQuery.data && manualQuery.data) {
      const dataWithContentChange = restructureDistributableStructure(changesQuery.data.content);
      setDistributableChanges(expandRowsWithContentChanges(dataWithContentChange));
    }
  }, [changesQuery.data, manualQuery.data, expandedRows]);

  useEffect(() => {
    if (draftManualQuery.data) {
      setDraftManual(draftManualQuery.data);
    }
  }, [draftManualQuery.data]);

  useEffect(() => {
    setSelectedChanges(distributableChanges.filter((x) => rowSelectionModel.includes(x.id)).map((x) => ({ ...x })));
  }, [distributableChanges, rowSelectionModel]);

  useEffect(() => {
    setSelectedTargets(
      distributableTargets.filter((x) => targetRowSelectionModel.includes(x.organization.id)).map((x) => ({ ...x }))
    );
  }, [distributableTargets, targetRowSelectionModel]);

  const handleSaveClick = () => {
    setIsSaving(true);
    const changesetsToDistribute = selectedTargets.flatMap((x) =>
      x.distributableChanges.flatMap((dc) => dc.changeSets)
    );
    distributeChanges(
      id!,
      changesetsToDistribute,
      () => {
        setIsSaving(false);
        enqueueSnackbar(
          parse(
            t('policy.issuedManuals.distributeChanges.successToast', '', { manualName: manualQuery.data?.name ?? '' })
          ),
          {
            variant: 'success',
            autoHideDuration: 6000,
          }
        );
        queryClient.invalidateQueries(['publishable-changes', id]);
        queryClient.invalidateQueries(['issued_manual']);
        navigate('/admin/policy/issued-manuals');
      },
      (errors) => {
        setIsSaving(false);
        enqueueSnackbar(errors.message, {
          variant: 'error',
          autoHideDuration: 20000,
        });
      }
    );
  };

  // Build quick lookup maps for parent/children relationships
  const parentIdToChildIds = useMemo(() => {
    const map = new Map<string, string[]>();
    distributableChanges.forEach((row) => {
      if (row.isMainRow && row.id) {
        const childIds = row.children.map((child) => child.id);
        map.set(row.id, childIds);
      }
    });
    return map;
  }, [distributableChanges]);

  const childIdToParentId = useMemo(() => {
    const map = new Map<string, string>();
    distributableChanges.forEach((row) => {
      if (row.isMainRow && row.id) {
        row.children.forEach((child) => {
          map.set(child.id, row.id);
        });
      }
    });
    return map;
  }, [distributableChanges]);

  // Controlled selection change with parent/child propagation
  const handleSelectionChange = (newModel: GridRowSelectionModel) => {
    const prev = new Set(rowSelectionModel as string[]);
    const next = new Set(newModel as string[]);

    // Detect added and removed ids
    const added: string[] = Array.from(next).filter((id) => !prev.has(id));
    const removed: string[] = Array.from(prev).filter((id) => !next.has(id));

    // Start with current selection
    const result = new Set(prev);

    // Handle removals first
    for (const id of removed) {
      result.delete(id);
      if (parentIdToChildIds.has(id)) {
        // Removing a parent deselects all its children
        for (const childId of parentIdToChildIds.get(id) || []) {
          result.delete(childId);
        }
      }
      const parentOfRemoved = childIdToParentId.get(id);
      if (parentOfRemoved) {
        // Removing a child may need to unset the parent
        result.delete(parentOfRemoved);
      }
    }

    // Handle additions
    for (const id of added) {
      result.add(id);
      if (parentIdToChildIds.has(id)) {
        // Adding a parent selects all its children
        for (const childId of parentIdToChildIds.get(id) || []) {
          result.add(childId);
        }
      }
      const parentOfAdded = childIdToParentId.get(id);
      if (parentOfAdded) {
        // If all children of this parent are selected, also select the parent
        const allChildren = parentIdToChildIds.get(parentOfAdded) || [];
        const allSelected = allChildren.every((cid) => result.has(cid));
        if (allSelected) {
          result.add(parentOfAdded);
        }
      }
    }

    setRowSelectionModel(Array.from(result));
  };

  const handleContinueClick = () => {
    setCurrentStep('select_targets');
    setIsLoadingTargets(true);

    // Build payload with one entry per selected content type (child row)
    // If both Content and Guide Sheet are selected for a policy, the policy appears twice
    const payload: DistributableChange[] = [];
    for (const idSel of rowSelectionModel as string[]) {
      // Check if this is a child row
      if (childIdToParentId.has(idSel)) {
        const parentId = childIdToParentId.get(idSel)!;
        const parent = distributableChanges.find((r) => r.isMainRow && r.id === parentId);
        const childRow = distributableChanges.find((r) => r.id === idSel);

        if (parent && childRow && childRow.currentDistributableChange) {
          payload.push({
            id: parentId,
            type: parent.type as DistributableChange['type'],
            title: parent.title,
            updatedAt: parent.updatedAt,
            lastDistributionAt: parent.lastDistributionAt || '',
            contentChange: childRow.currentDistributableChange.contentChange,
          });
        }
      } else if (parentIdToChildIds.has(idSel)) {
        // This is a parent row that is selected (not expanded or collapsed)
        // Include all its children in the payload
        const parent = distributableChanges.find((r) => r.isMainRow && r.id === idSel);

        if (parent && parent.children) {
          // Add an entry for each content type under this parent
          for (const child of parent.children) {
            payload.push({
              id: parent.id!,
              type: parent.type as DistributableChange['type'],
              title: parent.title,
              updatedAt: parent.updatedAt,
              lastDistributionAt: parent.lastDistributionAt || '',
              contentChange: child.contentChange,
            });
          }
        }
      }
    }

    getDistributeTargets(id || '0', payload, (data) => {
      setIsLoadingTargets(false);
      setDistributableTargets((prev) => {
        const newTargets: ExtendedDistributableTarget[] = [...prev, ...data].map((target) => ({
          ...target,
          searchableName: target.organization.name,
        }));
        return _.uniqBy(newTargets, (x) => x.organization.id);
      });
      // TODO: if data.count > distributableTargets get the next page of targets.
      // we need all targets so we can do filtering client side
    });
  };

  const localizeContentChange = (contentChange: string) => {
    let localized: string = '';
    switch (contentChange) {
      case 'CONTENT':
        localized = t('policy.issuedManuals.distributeChanges.contentTypes.CONTENT');
        break;
      case 'RELEASE_NOTE':
        localized = t('policy.issuedManuals.distributeChanges.contentTypes.RELEASE_NOTE');
        break;
      case 'GUIDE_SHEET':
        localized = t('policy.issuedManuals.distributeChanges.contentTypes.GUIDE_SHEET');
        break;
      default:
        localized = contentChange;
    }
    return localized;
  };

  const expandRowsWithContentChanges = (distributableChanges: DistributableParentRow[]) => {
    const expandedData: ExpandedDistributableChanges[] = [];

    distributableChanges.forEach((item) => {
      if (!item.id) return;

      const itemId = item.id;
      const isExpanded = expandedRows.has(itemId);

      if (item.children.length > 0 && isExpanded) {
        // Add the main row - stays the same as when not expanded (showing all content types)
        expandedData.push({
          ...item,
          isMainRow: true,
          parentId: itemId,
          distributableChangeIndex: 0,
          currentDistributableChange: null, // No specific content type - shows all like when collapsed
          isExpanded: true,
          searchableTitle: manualHelper.formatManualNodeTitle(item, draftManual! as any, true) as string,
        });

        // Add additional rows for each content type (one per contentChange)
        for (let i = 0; i < item.children.length; i++) {
          expandedData.push({
            ...item,
            id: item.children[i].id,
            isMainRow: false,
            parentId: itemId,
            distributableChangeIndex: i,
            currentDistributableChange: item.children[i],
            isExpanded: true,
            searchableTitle: localizeContentChange(item.children[i].contentChange),
          });
        }
      } else {
        // Add the regular row (not expanded or single content type)
        expandedData.push({
          ...item,
          isMainRow: true,
          parentId: itemId,
          distributableChangeIndex: 0,
          currentDistributableChange: item.children.length === 1 ? item.children[0] : null,
          isExpanded: false,
          searchableTitle: manualHelper.formatManualNodeTitle(item, draftManual! as any, true) as string,
        });
      }
    });

    return expandedData;
  };

  const selectChangesColumns: ColumnDef = useMemo(
    () => ({
      title: {
        header: t('policy.issuedManuals.distributeChanges.selectChanges.columns.title'),
        flex: 3,
        renderer: (params: GridCellParams) => {
          const row = params.row as ExpandedDistributableChanges;
          if (!row || !row.isMainRow) {
            return (
              <Box sx={{ pl: 6 }}>
                <span>{localizeContentChange(row.currentDistributableChange.contentChange)}</span>
              </Box>
            );
          }

          const originalVersion = row;
          const hasMultipleChildren = originalVersion.children.length > 0;

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {hasMultipleChildren && (
                <IconButton
                  size='small'
                  onClick={(e) => {
                    if (row.parentId) {
                      e.stopPropagation();
                      toggleRowExpansion(row.parentId);
                    }
                  }}
                  sx={{
                    p: 0.25,
                    minWidth: 'auto',
                    '& .MuiSvgIcon-root': {
                      fontSize: '1rem',
                    },
                  }}
                >
                  {row.isExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              )}
              <span>{manualHelper.formatManualNodeTitle(row, draftManual! as any)}</span>
            </Box>
          );
        },
        valueGetter: (value: any, row: any) => {
          return manualHelper.formatManualNodeTitle(row, draftManual! as any);
        },
      },
      updatedAt: {
        header: t('policy.issuedManuals.distributeChanges.selectChanges.columns.updatedAt'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row as ExpandedDistributableChanges;
          if (!row || !row.isMainRow) return null;
          return <span>{datetimeHelper.formatDateTime(row.updatedAt)}</span>;
        },
      },
      lastDistributionAt: {
        header: t('policy.issuedManuals.distributeChanges.selectChanges.columns.lastDistributionAt'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row || !row.isMainRow) {
            return (
              <span className='font-bold'>
                {datetimeHelper.formatDateTime(row.currentDistributableChange.lastDistributionAt)}
              </span>
            );
          }
          return <span>{datetimeHelper.formatDateTime(row.lastDistributionAt)}</span>;
        },
      },
    }),
    [t, draftManual]
  );

  const selectTargetsColumns: ColumnDef = useMemo(
    () => ({
      name: {
        header: t('policy.issuedManuals.distributeChanges.selectTargets.columns.name'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;

          const originalVersion = row;
          return <span>{row.organization.name}</span>;
        },
        valueGetter: (value: any, row: any) => {
          return row.organization.name;
        },
      },
    }),
    [t, manualQuery.data]
  );

  const toggleRowExpansion = (rowId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Filter distributableChanges by search
  const filteredDistributableChanges = useMemo(() => {
    if (changesSearch && changesSearch.trim() !== '') {
      const searchLower = changesSearch.toLowerCase();
      return distributableChanges.filter((item) => item.searchableTitle?.toLowerCase().includes(searchLower));
    }
    return distributableChanges;
  }, [distributableChanges, changesSearch]);

  // Filter distributableTargets by search
  const filteredDistributableTargets = useMemo(() => {
    if (targetSearch && targetSearch.trim() !== '') {
      const searchLower = targetSearch.toLowerCase();
      return distributableTargets.filter((item) => item.searchableName?.toLowerCase().includes(searchLower));
    }
    return distributableTargets;
  }, [distributableTargets, targetSearch]);

  return (
    <TakeoverLayout
      backUrl='/admin/policy/issued-manuals'
      pageTitle={manualQuery.data ? parse(manualQuery.data?.name ?? '') : ''}
      pageToolbarLeft={
        <Stack
          direction='row'
          spacing={3}
        >
          {currentStep === 'select_changes' && (
            <QuickSearch
              id='changes_search'
              initialValue={changesSearch}
              onChange={(val) => setChangesSearch(val)}
              placeholder={t('policy.issuedManuals.distributeChanges.search')}
            />
          )}
          {currentStep === 'select_targets' && (
            <QuickSearch
              id='target_search'
              initialValue={targetSearch}
              onChange={(val) => setTargetSearch(val)}
              placeholder={t('policy.issuedManuals.distributeChanges.selectTargets.search')}
            />
          )}
        </Stack>
      }
      footerContent={
        currentStep === 'select_changes' ? (
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', pb: 2, width: '100%' }}>
            <Button
              variant='outlined'
              color='neutral'
              onClick={() => navigate('/admin/policy/issued-manuals')}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleContinueClick}
              disabled={isSaving || selectedChanges.length === 0}
              component='button'
              type='submit'
              variant='contained'
            >
              {t('common.continue')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', pb: 2, width: '100%' }}>
            <Button
              variant='outlined'
              color='neutral'
              onClick={() => navigate('/admin/policy/issued-manuals')}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='outlined'
              color='neutral'
              onClick={() => setCurrentStep('select_changes')}
              startIcon={<ArrowBackOutlined />}
            >
              {t('common.back')}
            </Button>
            <Button
              onClick={handleSaveClick}
              disabled={isSaving || selectedTargets.length === 0}
              component='button'
              type='submit'
              variant='contained'
              endIcon={isSaving ? <CircularProgress size={16} /> : null}
            >
              {t('policy.issuedManuals.distributeChanges.save')}
            </Button>
          </Box>
        )
      }
    >
      {draftManual && currentStep === 'select_changes' && (
        <Stack spacing={2}>
          <Box sx={{ px: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'start',
                gap: 3,
              }}
            >
              {selectedChanges.length > 3 && (
                <Button
                  className='xs'
                  variant='outlined'
                  size='small'
                  onClick={() => setShowSelectedChanges(!showSelectedChanges)}
                  endIcon={
                    showSelectedChanges ? null : (
                      <Badge
                        variant='inline'
                        color='primary'
                        badgeContent={selectedChanges.length.toString()}
                      />
                    )
                  }
                >
                  {showSelectedChanges
                    ? t('policy.issuedManuals.distributeChanges.selectChanges.showLess')
                    : t('policy.issuedManuals.distributeChanges.selectChanges.showAll')}
                </Button>
              )}
              {selectedChanges.length > 0 && (
                <>
                  <Button
                    className='xs'
                    variant='text'
                    size='small'
                    onClick={() => setRowSelectionModel([])}
                  >
                    {t('policy.issuedManuals.distributeChanges.selectChanges.clearAll')}
                  </Button>
                  <Box
                    sx={{
                      height: '2rem',
                      border: `solid 1px ${neutral[300]}`,
                    }}
                  ></Box>
                </>
              )}
              {_.take(selectedChanges, showSelectedChanges ? selectedChanges.length : 3).map((change) => (
                <Chip
                  key={`selected_content_${change.id}`}
                  label={manualHelper.formatManualNodeTitle(change, manualQuery.data! as any)}
                  onDelete={() => setRowSelectionModel((prev) => prev.filter((x) => x !== change.id))}
                  deleteIcon={<RemoveIcon />}
                  size='small'
                  sx={{
                    justifyContent: 'space-between',
                    gap: 2,
                    height: '2rem',
                    minHeight: '2rem',
                    backgroundColor: neutral[50],
                    '& .MuiChip-label': {
                      lineHeight: '2rem',
                      padding: '0 8px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
          <GridSection
            columns={selectChangesColumns}
            data={filteredDistributableChanges}
            query={changesQuery}
            totalCount={filteredDistributableChanges.length}
            gridProps={{
              checkboxSelection: true,
              onRowSelectionModelChange: (newRowSelectionModel: GridRowSelectionModel) => {
                handleSelectionChange(newRowSelectionModel);
              },
              rowSelectionModel,
              // Add class to child rows to customize checkbox padding/indent
              getRowClassName: (params: any) => (params?.row?.isMainRow ? 'row-main' : 'row-child'),
              // Style the checkbox cell for child rows
              sx: {
                '& .MuiDataGrid-row.row-child .MuiDataGrid-cellCheckbox': {
                  pl: 6, // indent checkbox cell for children
                },
              },
            }}
          />
        </Stack>
      )}
      {currentStep === 'select_targets' && (
        <Stack spacing={2}>
          <Box sx={{ px: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'start',
                gap: 3,
              }}
            >
              {selectedTargets.length > 3 && (
                <Button
                  className='xs'
                  variant='outlined'
                  size='small'
                  onClick={() => setShowSelectedTargets(!showSelectedTargets)}
                  endIcon={
                    showSelectedTargets ? null : (
                      <Badge
                        variant='inline'
                        color='primary'
                        badgeContent={selectedTargets.length.toString()}
                      />
                    )
                  }
                >
                  {showSelectedTargets
                    ? t('policy.issuedManuals.distributeChanges.selectTargets.showLess')
                    : t('policy.issuedManuals.distributeChanges.selectTargets.showAll')}
                </Button>
              )}
              {selectedTargets.length > 0 && (
                <>
                  <Button
                    className='xs'
                    variant='text'
                    size='small'
                    onClick={() => setTargetRowSelectionModel([])}
                  >
                    {t('policy.issuedManuals.distributeChanges.selectTargets.clearAll')}
                  </Button>
                  <Box
                    sx={{
                      height: '32px',
                      border: `solid 1px ${neutral[300]}`,
                    }}
                  ></Box>
                </>
              )}
              {_.take(selectedTargets, showSelectedTargets ? selectedTargets.length : 3).map((target) => (
                <Chip
                  key={`selected_target_${target.organization.id}`}
                  label={target.organization.name}
                  onDelete={() =>
                    setTargetRowSelectionModel((prev) => prev.filter((x) => x !== target.organization.id))
                  }
                  deleteIcon={<RemoveIcon />}
                  size='small'
                  sx={{
                    justifyContent: 'space-between',
                    gap: 2,
                    height: '2rem',
                    minHeight: '2rem',
                    backgroundColor: neutral[50],
                    '& .MuiChip-label': {
                      lineHeight: '2rem',
                      padding: '0 8px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
          <GridSection
            columns={selectTargetsColumns}
            data={filteredDistributableTargets}
            query={changesQuery}
            isLoading={isLoadingTargets}
            totalCount={filteredDistributableTargets.length}
            gridProps={{
              checkboxSelection: true,
              onRowSelectionModelChange: (newTargetRowSelectionModel: GridRowSelectionModel) => {
                setTargetRowSelectionModel(newTargetRowSelectionModel);
              },
              rowSelectionModel: targetRowSelectionModel,
              getRowId: (row: any) => row.organization.id,
            }}
          />
        </Stack>
      )}
    </TakeoverLayout>
  );
};

export default DistributeChanges;
